// A model's status in the Models surface's words
// (02-DESIGN/05-inference/01-models-and-instances.md § the console: *ready*,
// *starting*, *stopped*, *not configured* when settings or credential are
// missing, *ambiguous* when another row carries the same name; § an instance
// is a workload: an instance is not ready until its settings validate and its
// credential is readable, and until then the fold shows the placement running
// with no model behind it, which the console names; *starting* with the
// callout named beside it while the required document names a callout the
// instance has not found — § the required document: an instance is not ready
// until it has found its callout). Pure, so that the
// derivation is tested without a connection.

import type { Tone } from "../../ui/components.tsx";
import { RegistrationState, Status, type Workload } from "../../wire/workloads.ts";

/** Facts are what the page knows about one model. */
export interface Facts {
  /** The workload's registration state and its placement's status. */
  workload: Pick<Workload, "state" | "placement">;
  /** Whether discovery answered for this workload: the one sign the model is ready. */
  discovered: boolean;
  /** Whether the settings document exists. */
  hasSettings: boolean;
  /** Whether the credential exists at the workload's api-key path. */
  hasKey: boolean;
  /** Whether another model carries the same name. */
  ambiguous: boolean;
  /**
   * The callout the required document names, when it names one, and whether
   * a callout service of that name answered the console's discovery:
   * undefined when the console could not ask.
   */
  callout?: { name: string; found: boolean | undefined };
}

/** Plain is the status as the page says it. */
export interface Plain {
  word: string;
  tone: Tone;
  /** Whether it belongs on Home under *needs you*. */
  attention: boolean;
  /** What is said beside the word, when the word alone does not say why. */
  beside?: string;
}

/** modelStatus derives the one word the status column shows. */
export function modelStatus(f: Facts): Plain {
  if (f.workload.state === RegistrationState.retired) {
    return { word: "removed", tone: "neutral", attention: false };
  }
  if (f.workload.state === RegistrationState.suspended) {
    return { word: "stopped", tone: "neutral", attention: false };
  }
  if (f.ambiguous) {
    return { word: "ambiguous", tone: "bad", attention: true };
  }
  if (f.discovered) {
    return { word: "ready", tone: "live", attention: false };
  }
  if (!f.hasSettings || !f.hasKey) {
    return { word: "not configured", tone: "bad", attention: true };
  }
  const status: string = f.workload.placement.status;
  switch (status) {
    case Status.failing:
      return { word: "failing", tone: "bad", attention: true };
    case Status.unschedulable:
      return { word: "cannot start", tone: "bad", attention: true };
    case Status.suspended:
      return { word: "stopped", tone: "neutral", attention: false };
    case Status.retired:
      return { word: "removed", tone: "neutral", attention: false };
    default:
      // pending, placing, and running without a model behind it yet
      return f.callout === undefined
        ? { word: "starting", tone: "info", attention: false }
        : calloutStatus(f.callout);
  }
}

/**
 * ambiguousNames answers the names more than one model carries: what the
 * platform cannot prevent at write, a caller refuses rather than picks, and
 * the console marks both rows.
 */
export function ambiguousNames(names: readonly (string | undefined)[]): Set<string> {
  const seen = new Map<string, number>();
  for (const n of names) {
    if (n !== undefined && n !== "") {
      seen.set(n, (seen.get(n) ?? 0) + 1);
    }
  }
  return new Set([...seen.entries()].filter(([, c]) => c > 1).map(([n]) => n));
}

/**
 * calloutStatus is *starting* with the callout named beside it: a model whose
 * required document names a callout is not ready until it has found it, and
 * one whose callout nobody serves refuses every request — which needs
 * someone, so it is marked.
 */
export function calloutStatus(c: { name: string; found: boolean | undefined }): Plain {
  if (c.found === false) {
    return {
      word: "starting",
      tone: "bad",
      attention: true,
      beside: `${c.name}, which must inspect every request first, is not running: this model refuses every request until it is added`,
    };
  }
  return {
    word: "starting",
    tone: "info",
    attention: false,
    beside: `waiting for ${c.name}, which inspects every request first`,
  };
}
